import { useRef, useState, ReactNode, CSSProperties } from 'react'
import { Tooltip } from '@mui/material'

interface OverflowTextProps {
  children: ReactNode
  /**
   * Tooltip text shown when the content overflows. Defaults to `children` when
   * it is a plain string. Required when `children` is not a string.
   */
  title?: string
  /** Additional inline styles merged onto the wrapper span. */
  style?: CSSProperties
  className?: string
}

/**
 * Renders text that truncates with an ellipsis and shows a tooltip on hover —
 * but only when the content is actually overflowing.
 *
 * The parent must constrain the width (e.g. flex item with `min-width: 0`,
 * a fixed-layout table cell, or any block with a bounded width). Without a
 * width bound, the span will grow to fit and truncation won't activate.
 *
 * @example
 * ```tsx
 * <Box sx={{ display: 'flex', minWidth: 0 }}>
 *   <OverflowText>{file.name}</OverflowText>
 * </Box>
 * ```
 */
export function OverflowText({ children, title, style, className }: OverflowTextProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const [showTooltip, setShowTooltip] = useState(false)

  const tooltipText = title ?? (typeof children === 'string' ? children : '')

  const handleMouseEnter = () => {
    const el = ref.current
    if (el) {
      setShowTooltip(el.scrollWidth > el.clientWidth)
    }
  }

  return (
    <Tooltip title={showTooltip ? tooltipText : ''} disableInteractive>
      <span
        ref={ref}
        className={className}
        style={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...style,
        }}
        onMouseEnter={handleMouseEnter}
      >
        {children}
      </span>
    </Tooltip>
  )
}
